// docs/92 HM6: the ground under a commissioned huge body, one quad at art resolution rendered to a
// texture (as the weather's ground is, docs/20) and laid flat under its feet.
//
// uKind 0, a red dragon: the earth under it is cooking — a web of thin molten cracks (ridged noise)
// that glow and flicker, brightest close in, ember-flecks winking in them, and a soft heat bloom.
// Drawn with added light, so night only makes it brighter.
// uKind 1, a mountain troll: the earth has split under its weight — dark cracks over a dusty
// scuff, and a slow drift of dust. Drawn over the ground (premultiplied, Phaser's NORMAL blend).
//
// docs/94, each drawn over the ground (premultiplied, NORMAL) like the troll's:
// uKind 2, a yeti: hoarfrost creeping out from it in feathered crystals, wind-blown snow drifting
// over it, and glints winking in the rime.
// uKind 3, a sandworm: the dunes churning round it — rings of sand rolling outward over a dark pit
// where it went down, grains shifting as they go.
// uKind 4, the spider queen: her web — spokes and a spiral of silk, sagging, broken here and there,
// dew glinting on it, over a dark stain where she sits.
// uKind 5, a basilisk: a pool of swamp water — murky, a slow sheen, mist drifting over it and
// bubbles rising and popping.
// uKind 6, a black dragon: a pool of darkness with smoky edges, dull red embers breathing in veins
// beneath it.
//
// docs/95, each drawn over the ground too:
// uKind 7, a great eagle: it hovers, so its shadow lies on the ground under it, drifting as it
// circles and swelling as it bobs, and the wind off its wings streaks the ground in passing gusts.
// uKind 8, an ent: a mossy forest floor, roots spreading out from it and slowly creeping further,
// leaf litter, a few sprouts winking green.
// uKind 9, a grim giant: frozen flagstones, rimed at the joints, and a circle of runes cut round it
// glowing a cold blue, a pulse of light running round the ring.
//
// docs/116, the deep's two, drawn over the ground too:
// uKind 10, the kraken: dark churning deep water — swells rolling round it, a slow ink swirl turning
// in it (spiral arms of black), a ring of foam breaking round its footprint and flecks of spray.
// uKind 11, the maw of the deep: black abyssal water, a violet glow pulsing up from far below it,
// ripples running out, and faint orange eye-glints opening and closing in the dark.
//
// uFade takes it out as the body falls.
export const HUGE_GROUND_FRAG = `
#pragma phaserTemplate(shaderName)
#pragma phaserTemplate(extensions)
#pragma phaserTemplate(features)
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
#pragma phaserTemplate(fragmentDefine)
varying vec2 outTexCoord;
#pragma phaserTemplate(outVariables)
#pragma phaserTemplate(fragmentHeader)
uniform vec2 uSize;   // the quad in art pixels
uniform float uTime;  // seconds (frozen under reduce motion)
uniform float uKind;  // 0 dragon, 1 troll, 2 yeti, 3 sandworm, 4 spider queen, 5 basilisk, 6 black dragon,
                      // 7 great eagle, 8 ent, 9 grim giant, 10 kraken, 11 maw of the deep
uniform float uFade;  // 1 standing .. 0 fallen

float hash(vec2 p) { p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}
float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 4; i++) { v += a * noise(p); p = p * 2.07 + vec2(11.3, 4.7); a *= 0.5; }
  return v;
}
// thin bright lines where the noise crosses its middle: cracks
float ridges(vec2 p) { return pow(1.0 - abs(fbm(p) * 2.0 - 1.0), 7.0); }

void main() {
  // snap to art pixels so it reads as pixel art, like the tiles round it
  vec2 px = floor(outTexCoord * uSize) + 0.5;
  vec2 uv = px / uSize;
  vec2 c = (uv - 0.5) * 2.0;
  float r = length(c);
  float fall = smoothstep(1.0, 0.25, r);
  vec4 col = vec4(0.0);
  if (uKind < 0.5) {
    float t = uTime;
    vec2 q = c * 2.6 + vec2(0.0, t * 0.05);
    float crack = ridges(q + 0.15 * vec2(fbm(q * 1.7 + t * 0.2), fbm(q * 1.7 - t * 0.17)));
    float flicker = 0.75 + 0.25 * sin(t * 6.0 + fbm(q * 3.0) * 9.0);
    float fleck = step(0.985, hash(floor(px / 2.0) + floor(t * 3.0))) * fall;
    float bloom = smoothstep(1.0, 0.0, r) * 0.28 * (0.85 + 0.15 * sin(t * 1.3));
    float heat = crack * flicker * fall + bloom + fleck;
    vec3 hot = mix(vec3(0.55, 0.08, 0.02), vec3(1.0, 0.62, 0.18), clamp(crack * 1.4, 0.0, 1.0));
    hot = mix(hot, vec3(1.0, 0.92, 0.6), fleck);
    col = vec4(hot * heat, 0.0);
  } else if (uKind < 1.5) {
    vec2 q = c * 2.2;
    float crack = ridges(q * 1.3);
    float scuff = smoothstep(1.0, 0.2, r) * (0.35 + 0.25 * fbm(q * 3.0));
    float dust = fbm(q * 1.5 + vec2(uTime * 0.07, -uTime * 0.04)) * smoothstep(1.0, 0.4, r) * 0.18;
    float a = clamp(scuff * 0.45 + crack * fall * 0.75, 0.0, 0.85);
    vec3 earth = mix(vec3(0.24, 0.19, 0.14), vec3(0.06, 0.04, 0.03), clamp(crack * 1.2, 0.0, 1.0));
    earth = mix(earth, vec3(0.62, 0.56, 0.46), dust);
    float aa = clamp(a + dust * 0.6, 0.0, 0.9);
    col = vec4(earth * aa, aa);
  } else if (uKind < 2.5) {
    // rime: a ragged patch of frost, feathered crystals, snow blowing across, glints
    float t = uTime;
    vec2 q = c * 2.4;
    float patch = smoothstep(1.0, 0.15, r + 0.4 * (fbm(q * 1.6) - 0.5));
    float feather = ridges(q * 2.3 + 0.6 * vec2(fbm(q * 3.1), fbm(q * 2.7 + 5.0)));
    float drift = smoothstep(0.55, 0.85, fbm(q * vec2(0.9, 2.2) + vec2(t * 0.35, t * 0.04))) * smoothstep(1.0, 0.3, r);
    float glint = step(0.991, hash(floor(px) + floor(t * 2.5))) * patch;
    vec3 ice = mix(vec3(0.62, 0.76, 0.90), vec3(0.93, 0.97, 1.0), clamp(feather + drift, 0.0, 1.0));
    ice = mix(ice, vec3(1.0), glint);
    float a = clamp(patch * 0.5 + feather * patch * 0.4 + drift * 0.35 + glint, 0.0, 0.92);
    col = vec4(ice * a, a);
  } else if (uKind < 3.5) {
    // churning sand: rings rolling outward, warped, over a dark pit, grains shifting
    float t = uTime;
    vec2 q = c * 2.0;
    float warp = fbm(q * 1.8 + vec2(t * 0.1, -t * 0.08));
    float band = 0.5 + 0.5 * sin(r * 17.0 - t * 2.2 + warp * 4.0);
    float churn = smoothstep(1.0, 0.3, r + 0.25 * (warp - 0.5));
    float pit = smoothstep(0.4, 0.05, r);
    vec3 sand = mix(vec3(0.55, 0.42, 0.26), vec3(0.82, 0.70, 0.47), band);
    sand += (hash(floor(px) + floor(t * 6.0)) - 0.5) * 0.12 * churn;
    sand = mix(sand, vec3(0.14, 0.10, 0.06), pit * 0.85);
    float a = clamp(churn * (0.42 + 0.22 * band) + pit * 0.4, 0.0, 0.9);
    col = vec4(sand * a, a);
  } else if (uKind < 4.5) {
    // a web: twelve swaying spokes and a sagging spiral, broken in places, dew glinting on it
    float t = uTime;
    float R = uSize.x * 0.5;
    float ang = atan(c.y, c.x);
    float sway = 0.03 * sin(t * 0.8 + r * 3.0);
    float sa = ang * 6.0 / 3.14159 + sway;
    float spokeD = abs(fract(sa) - 0.5) * 2.0;                    // 0 on a spoke .. 1 between
    float spoke = step(abs(sin((ang + sway) * 6.0)) * r * R / 6.0, 0.6) * step(0.08, r);
    float sag = 1.0 - 0.06 * spokeD;
    float turn = r * sag * 7.0 + ang / 6.28318;
    float ring = step(abs(fract(turn + 0.5) - 0.5) * (R / 7.0), 0.55) * step(0.12, r);
    float broken = step(0.82, hash(vec2(floor(sa), floor(turn))));
    float silk = max(spoke, ring * (1.0 - broken)) * smoothstep(1.0, 0.82, r);
    float dew = step(0.97, hash(floor(px / 2.0) + floor(t * 1.5))) * silk;
    float stain = smoothstep(0.75, 0.0, r) * (0.25 + 0.1 * fbm(c * 4.0));
    vec3 rgb = mix(vec3(0.08, 0.03, 0.06), vec3(0.86, 0.86, 0.92), silk);
    rgb = mix(rgb, vec3(1.0), dew);
    float a = clamp(stain + silk * 0.6 + dew, 0.0, 0.85);
    col = vec4(rgb * a, a);
  } else if (uKind < 5.5) {
    // swamp water: a murky pool, a slow sheen, mist, bubbles rising and popping
    float t = uTime;
    vec2 q = c * 2.2;
    float edge = smoothstep(1.0, 0.25, r + 0.45 * (fbm(q * 1.4) - 0.5));
    float murk = fbm(q * 2.0 + vec2(t * 0.05, t * 0.03));
    vec3 water = mix(vec3(0.07, 0.12, 0.06), vec3(0.20, 0.28, 0.10), murk);
    float sheen = pow(fbm(q * 3.0 + vec2(-t * 0.12, t * 0.07)), 3.0) * 1.6;
    water += vec3(0.22, 0.32, 0.12) * sheen * edge;
    vec2 cell = floor(q * 2.5);
    float h = hash(cell);
    float life = fract(t * 0.35 + h * 7.0);
    float bub = step(0.6, h) * step(abs(length(fract(q * 2.5) - 0.5) - life * 0.32), 0.05) * (1.0 - life) * edge;
    float mist = smoothstep(0.45, 0.8, fbm(q * vec2(0.8, 2.0) + vec2(t * 0.12, 0.0))) * smoothstep(1.0, 0.4, r);
    water = mix(water, vec3(0.62, 0.70, 0.52), bub * 0.8 + mist * 0.5);
    float a = clamp(edge * 0.62 + bub * 0.3 + mist * 0.2, 0.0, 0.86);
    col = vec4(water * a, a);
  } else if (uKind < 6.5) {
    // darkness: a smoky-edged pool of shadow, dull embers breathing in veins beneath it
    float t = uTime;
    vec2 q = c * 2.4;
    float smoke = fbm(q * 1.6 + vec2(t * 0.06, -t * 0.11));
    float pool = smoothstep(1.0, 0.1, r + 0.5 * (smoke - 0.5));
    float vein = ridges(q * 1.1 + 0.2 * vec2(fbm(q * 2.0 - t * 0.05), fbm(q * 2.0 + t * 0.04)));
    float pulse = 0.5 + 0.5 * sin(t * 0.9 + fbm(q * 2.0) * 6.0);
    float v = vein * pulse * smoothstep(0.95, 0.25, r);
    vec3 dark = mix(vec3(0.07, 0.05, 0.07), vec3(0.015, 0.01, 0.02), smoothstep(0.7, 0.0, r));
    vec3 rgb = mix(dark, vec3(0.62, 0.12, 0.05), clamp(v * 1.3, 0.0, 1.0));
    float a = clamp(pool * 0.78 + v * 0.2, 0.0, 0.92);
    col = vec4(rgb * a, a);
  } else if (uKind < 7.5) {
    // the eagle's shadow on the ground, circling and breathing with its bob, and wind streaking past
    float t = uTime;
    vec2 off = vec2(cos(t * 0.45), sin(t * 0.45)) * vec2(0.14, 0.08);
    float bob = 0.5 + 0.5 * sin(t * 1.6);
    vec2 sc = (c - off) / vec2(0.62 + 0.06 * bob, 0.36 + 0.03 * bob);
    float sr = length(sc);
    // two wings and a body: wider than it is deep, the wingtips ragged
    float wing = smoothstep(1.0, 0.55, length(sc * vec2(0.55, 1.6)) + 0.12 * fbm(sc * 4.0 + t * 0.2));
    float body = smoothstep(0.55, 0.2, sr);
    float shade = clamp(max(wing, body), 0.0, 1.0) * (0.42 - 0.1 * bob);
    vec2 w = vec2(c.x * 1.2 - t * 0.9, c.y * 7.0);
    float streak = smoothstep(0.62, 0.8, fbm(w + vec2(0.0, fbm(c * 2.0 + t * 0.1) * 1.5)));
    float gust = 0.5 + 0.5 * sin(t * 0.7 + c.y * 3.0);
    streak *= gust * smoothstep(1.0, 0.35, r);
    vec3 rgb = mix(vec3(0.05, 0.05, 0.07), vec3(0.86, 0.88, 0.84), clamp(streak * 1.4, 0.0, 1.0) * (1.0 - shade));
    float a = clamp(shade + streak * 0.28, 0.0, 0.7);
    col = vec4(rgb * a, a);
  } else if (uKind < 8.5) {
    // a mossy forest floor, roots creeping out from the ent, leaf litter, sprouts
    float t = uTime;
    vec2 q = c * 2.3;
    float patch = smoothstep(1.0, 0.2, r + 0.4 * (fbm(q * 1.5) - 0.5));
    float moss = fbm(q * 3.2 + 3.0);
    vec3 floor_ = mix(vec3(0.16, 0.22, 0.08), vec3(0.34, 0.46, 0.16), moss);
    float ang = atan(c.y, c.x);
    // roots: a dozen winding ridges running outward, reaching a little further and back as it breathes
    float wind = ang * 12.0 / 6.28318 + 0.9 * fbm(vec2(r * 3.0, ang * 2.0));
    float root = pow(1.0 - abs(fract(wind) - 0.5) * 2.0, 10.0 + 18.0 * r);
    float reach = 0.78 + 0.12 * sin(t * 0.25 + floor(wind) * 1.7) + 0.06 * sin(t * 0.07);
    root *= smoothstep(reach, reach - 0.18, r) * step(0.08, r);
    vec3 bark = mix(vec3(0.24, 0.16, 0.08), vec3(0.42, 0.30, 0.16), fbm(q * 6.0));
    floor_ = mix(floor_, bark, clamp(root * 1.5, 0.0, 1.0));
    // leaf litter: little russet and gold flecks
    float h = hash(floor(px / 2.0));
    float leaf = step(0.93, h) * patch;
    floor_ = mix(floor_, mix(vec3(0.62, 0.32, 0.10), vec3(0.78, 0.62, 0.18), fract(h * 37.0)), leaf);
    float sprout = step(0.993, hash(floor(px) + 7.0)) * (0.5 + 0.5 * sin(t * 2.0 + h * 40.0)) * patch;
    floor_ = mix(floor_, vec3(0.62, 0.92, 0.36), sprout);
    float a = clamp(patch * 0.6 + root * 0.35 + leaf * 0.2 + sprout, 0.0, 0.9);
    col = vec4(floor_ * a, a);
  } else if (uKind < 9.5) {
    // frozen flagstones, rime in the joints, and a circle of runes round the giant glowing cold blue
    float t = uTime;
    vec2 q = c * 3.2;
    float patch = smoothstep(1.0, 0.25, r + 0.35 * (fbm(c * 3.0) - 0.5));
    vec2 cell = fract(q + vec2(0.5 * step(1.0, mod(floor(q.y), 2.0)), 0.0));
    float joint = 1.0 - smoothstep(0.0, 0.08, min(min(cell.x, 1.0 - cell.x), min(cell.y, 1.0 - cell.y)));
    vec3 stone = mix(vec3(0.30, 0.33, 0.38), vec3(0.46, 0.50, 0.56), fbm(q * 2.0));
    stone = mix(stone, vec3(0.84, 0.92, 1.0), joint * 0.7);
    float frost = ridges(c * 5.0) * 0.5;
    stone = mix(stone, vec3(0.9, 0.96, 1.0), frost);
    // the ring: two thin circles and, between them, a rune in each of sixteen cells
    float ang = atan(c.y, c.x);
    float band = smoothstep(0.035, 0.0, abs(r - 0.66)) + smoothstep(0.035, 0.0, abs(r - 0.86));
    float seg = ang * 16.0 / 6.28318;
    float id = floor(seg);
    vec2 g = vec2(fract(seg), (r - 0.69) / 0.14);
    float inside = step(0.0, g.y) * step(g.y, 1.0) * step(0.18, g.x) * step(g.x, 0.82);
    vec2 gc = floor(vec2((g.x - 0.18) / 0.64, g.y) * 3.0);
    float stroke = step(0.45, hash(gc + id * 3.1)) * inside;
    float run = 0.5 + 0.5 * cos(ang - t * 0.9);
    float glow = clamp(band + stroke, 0.0, 1.0) * (0.45 + 0.55 * pow(run, 3.0)) * (0.85 + 0.15 * sin(t * 2.3));
    vec3 rgb = mix(stone, vec3(0.55, 0.85, 1.0), glow);
    rgb += vec3(0.2, 0.35, 0.5) * glow * pow(run, 4.0);
    float a = clamp(patch * 0.55 + glow * 0.45, 0.0, 0.92);
    col = vec4(rgb * a, a);
  } else if (uKind < 10.5) {
    // the kraken's deep water: swells, an ink swirl turning in it, a ring of foam breaking round it
    float t = uTime;
    vec2 q = c * 2.2;
    float edge = smoothstep(1.0, 0.3, r + 0.35 * (fbm(q * 1.3 + t * 0.05) - 0.5));
    float swell = fbm(q * 1.6 + vec2(t * 0.12, -t * 0.08));
    vec3 water = mix(vec3(0.03, 0.10, 0.16), vec3(0.08, 0.24, 0.32), swell);
    // the swirl: three spiral arms of ink, wound in and turning slowly
    float ang = atan(c.y, c.x);
    float spiral = sin(ang * 3.0 + r * 9.0 - t * 0.6 + fbm(q * 2.0) * 2.0);
    float ink = smoothstep(0.35, 0.95, spiral) * smoothstep(0.85, 0.1, r);
    water = mix(water, vec3(0.01, 0.015, 0.03), ink * 0.85);
    // foam: a broken ring round the footprint, washing in and out, and flecks of spray
    float fr = 0.62 + 0.04 * sin(t * 0.9) + 0.06 * (fbm(vec2(ang * 3.0, t * 0.3)) - 0.5);
    float foam = smoothstep(0.07, 0.0, abs(r - fr)) * smoothstep(0.35, 0.65, fbm(vec2(ang * 5.0, r * 6.0) + t * 0.4));
    float fleck = step(0.985, hash(floor(px / 2.0) + floor(t * 4.0))) * edge;
    float sheen = pow(fbm(q * 3.0 + vec2(-t * 0.2, t * 0.1)), 4.0) * 1.4 * (1.0 - ink);
    water += vec3(0.20, 0.36, 0.42) * sheen * edge;
    water = mix(water, vec3(0.88, 0.96, 0.98), clamp(foam + fleck, 0.0, 1.0));
    float a = clamp(edge * 0.72 + foam * 0.3 + fleck, 0.0, 0.9);
    col = vec4(water * a, a);
  } else {
    // the maw's abyss: black water, a violet glow pulsing up from below, ripples, eye-glints in the dark
    float t = uTime;
    vec2 q = c * 2.3;
    float edge = smoothstep(1.0, 0.2, r + 0.4 * (fbm(q * 1.4) - 0.5));
    float murk = fbm(q * 1.8 + vec2(t * 0.04, -t * 0.06));
    vec3 water = mix(vec3(0.02, 0.01, 0.03), vec3(0.07, 0.04, 0.10), murk);
    float pulse = 0.55 + 0.45 * sin(t * 1.4);
    float deep = smoothstep(0.8, 0.0, r) * (0.5 + 0.5 * fbm(q * 2.4 - t * 0.2)) * pulse;
    water += vec3(0.38, 0.12, 0.62) * deep * 0.6;
    float ripple = smoothstep(0.75, 1.0, sin(r * 22.0 - t * 2.0 + murk * 3.0)) * smoothstep(1.0, 0.4, r) * 0.35;
    water += vec3(0.22, 0.12, 0.30) * ripple;
    // eyes: a few cells open for a moment, a slit of orange in the dark
    vec2 cell = floor(q * 3.0);
    float h = hash(cell);
    float open = step(0.86, h) * smoothstep(0.0, 0.15, sin(t * (0.7 + h) + h * 40.0) - 0.85);
    vec2 f = fract(q * 3.0) - 0.5;
    float slit = smoothstep(0.12, 0.0, length(f * vec2(1.0, 3.0)));
    float eye = open * slit * smoothstep(0.95, 0.35, r);
    water = mix(water, vec3(1.0, 0.55, 0.12), clamp(eye * 1.5, 0.0, 1.0));
    float a = clamp(edge * 0.8 + deep * 0.1 + eye, 0.0, 0.92);
    col = vec4(water * a, a);
  }
  gl_FragColor = col * uFade;
}
`;
